// Noticing that the session ended, at any point after the app has started.
//
// The sign-in gate (session.tsx) runs ONCE, before React mounts, and resolves
// when there is a session. Nothing watched it after that — so when the access
// token expired, the app carried on rendering a signed-in shell while every
// request came back 401. The worklist emptied, tiles stopped loading, and the
// upload page reported that uploads were failing. All true, none of it the
// actual problem, and nothing on screen said "you are signed out".
//
// Community tokens last an hour (SESSION_TTL_SECONDS, 12 on enterprise), so
// this is not an edge case: it is what happens to anybody who leaves a tab open
// over lunch.
//
// A FETCH WRAPPER rather than a check in each API client, because the clients
// are not the whole story — dicomweb-client issues its own requests for every
// tile, and a per-module check would miss exactly the surface that produces the
// most requests. One wrapper sees all of it.
//
// It does NOT reload. Reloading would re-run the gate and show the form, which
// is tidy and would also discard a queue of files somebody had just spent ten
// minutes selecting. The app is told instead, and offers the reload.
import { getConfig } from '../config/runtimeConfig';

/**
 * Is this request to OUR API?
 *
 * `url.startsWith(base)` alone is a trap: the base can be the EMPTY STRING for
 * a deployment whose API shares the page's origin, and every string starts
 * with the empty string — so the check passed for every URL on the page, and
 * one 401 from anything at all would have told the person they were signed out.
 *
 * With no base configured the API is same-origin by definition, so that is what
 * to test for; with one, compare against it.
 */
export function isOurApi(url: string, base: string): boolean {
  // Bounded, not a bare prefix. `startsWith(base)` alone matches
  // https://api.example.com.evil.test against base https://api.example.com —
  // the same class of mistake the empty-base branch below was just fixed for,
  // and leaving one loose while hardening the other is worse than either.
  if (base) return url === base || url.startsWith(`${base}/`) || url.startsWith(`${base}?`);
  return (
    url.startsWith('/') ||
    !/^[a-z][a-z0-9+.-]*:/i.test(url) || // relative, no scheme
    (typeof window !== 'undefined' && url.startsWith(window.location.origin))
  );
}

/** The origin an API root names, or '' when it is relative (same-origin). */
function serverOrigin(apiRoot: string): string {
  try {
    return /^[a-z][a-z0-9+.-]*:/i.test(apiRoot) ? new URL(apiRoot).origin : '';
  } catch {
    return '';
  }
}

type Listener = (ended: boolean) => void;

let ended = false;
let armed = false;
const listeners = new Set<Listener>();

/** True once any authenticated request has come back 401. */
export function sessionHasEnded(): boolean {
  return ended;
}

export function subscribeSessionEnded(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Record a 401 from an authenticated request.
 *
 * Exported so the XHR path can report one too: part uploads use
 * XMLHttpRequest for progress events, and the wrapper below cannot see them.
 */
export function noteUnauthorized(): void {
  if (!armed || ended) return;
  ended = true;
  for (const fn of listeners) fn(true);
}

/**
 * Start watching. Called AFTER the sign-in gate resolves, and that ordering is
 * load-bearing: /auth/login answers 401 for a wrong password, and arming this
 * beforehand would put a "your session ended" notice over the sign-in form of
 * somebody who has not signed in yet.
 */
export function watchForSessionEnd(): void {
  if (armed) return;
  armed = true;

  // The SERVER's origin, not the /api root: a 401 from a tile under
  // /dicom-web says the session ended just as surely as one from /api.
  const base = serverOrigin(getConfig().apiRoot);
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const res = await original(input, init);
    if (res.status === 401) {
      // OUR API only. A 401 from somewhere else on the page — a third-party
      // image, a probe — says nothing about this session.
      const url =
        typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      if (isOurApi(url, base)) noteUnauthorized();
    }
    return res;
  };
}
